import {
  forwardRef,
  useEffect,
  useState,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { Input } from '@/components/atoms/input';
import { cn } from '@/lib/utils';

interface QuantityInputProps extends Omit<
  ComponentPropsWithoutRef<typeof Input>,
  'type' | 'value' | 'onChange'
> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Controlled number input that allows clearing while editing. It commits a
 * clamped numeric value on blur or Enter, avoiding `Number(value) || 1`
 * snap-back behavior during normal replacement edits.
 */
export const QuantityInput = forwardRef<HTMLInputElement, QuantityInputProps>(
  function QuantityInput(
    {
      value,
      onChange,
      min = 1,
      max,
      step = 1,
      placeholder,
      className,
      disabled,
      onBlur,
      onKeyDown,
      ...rest
    },
    ref,
  ) {
    const [draft, setDraft] = useState(String(value));

    useEffect(() => {
      setDraft((current) => {
        const currentNum = Number(current);
        if (Number.isFinite(currentNum) && currentNum === value) return current;
        return String(value);
      });
    }, [value]);

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
      setDraft(event.target.value);
    }

    function commit() {
      const parsed = Number(draft);
      let next = Number.isFinite(parsed) ? parsed : min;
      if (next < min) next = min;
      if (max != null && next > max) next = max;
      setDraft(String(next));
      if (next !== value) onChange(next);
    }

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
      commit();
      onBlur?.(event);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.blur();
        return;
      }
      onKeyDown?.(event);
    }

    return (
      <Input
        {...rest}
        ref={ref}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={step}
        value={draft}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(className)}
      />
    );
  },
);
